import React from 'react';
import { PriceCalculation } from '@idearoom/types';
import { Field, Form, InjectedFormProps, reduxForm } from 'redux-form';
import { useTranslation } from 'react-i18next';
import { Theme, Grid, MenuItem, FormControl, FormHelperText, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useAppSelector } from '../hooks';
import { Forms } from '../constants/Forms';
import { PricingComponentEditFields } from '../constants/FormFields';
import { InputField } from './redux-form/InputField';
import { RenderSelectField } from './RenderSelectField';
import { AppState } from '../types/AppState';
import { compoundCaseToTitleCase } from '../utils/stringUtils';
import { I18nKeys } from '../constants/I18nKeys';
import {
  DisplayColumns,
  PricingCalculationColumns,
  RegionPricingColumns,
  priceCalculationMap,
} from '../constants/ComponentPricing';

const useStyles = makeStyles((theme: Theme) => ({
  fieldContainer: {
    marginTop: theme.spacing(2),
  },
  field: {
    margin: '0px',
    padding: '0px',
  },
  helperText: {
    fontSize: 'inherit',
    fontWeight: 'inherit',
    letterSpacing: 'inherit',
    lineHeight: 'inherit',
  },
}));

export interface FormData {
  [PricingComponentEditFields.Component]: string;
  [DisplayColumns.Label]: string;
  [RegionPricingColumns.Price]: number;
  [PricingCalculationColumns.PriceCalculation]: string;
  [DisplayColumns.PriceExpression]: string;
}

interface CustomProps {
  formFields: (PricingComponentEditFields | DisplayColumns | RegionPricingColumns | PricingCalculationColumns)[];
  onSubmit: { (values: FormData): void };
}

type FormProps = CustomProps & InjectedFormProps<FormData, CustomProps>;

const PricingComponentEditFormComponent: React.FC<FormProps> = ({ handleSubmit, formFields, onSubmit }: FormProps) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const {
    component: { selectedComponentCategoryKey: componentCategoryKey = '' },
  } = useAppSelector((state: AppState) => state?.pricing);

  if (!componentCategoryKey) return null;

  const expressionExists = formFields.includes(DisplayColumns.PriceExpression);
  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Grid container rowGap={2} className={classes.fieldContainer}>
        {formFields.map((field, _, allFields) => {
          if (([DisplayColumns, RegionPricingColumns].flatMap((e) => Object.values(e)) as string[]).includes(field)) {
            const isExpressionField = DisplayColumns.PriceExpression === field;
            const isPriceField = (Object.values(RegionPricingColumns) as string[]).includes(field);

            const displayPricingCalculation =
              (Object.values(RegionPricingColumns) as string[]).includes(field) &&
              priceCalculationMap[field as string] &&
              allFields.includes(priceCalculationMap[field]);
            return (
              <Grid container direction="row" key={field} spacing={2}>
                {displayPricingCalculation && (
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <Field
                        component={RenderSelectField}
                        label={t(I18nKeys.PricingCalculationField)}
                        name={priceCalculationMap[field as string]}
                        variant="filled"
                        className={classes.field}
                        disabled
                        SelectProps={{
                          renderValue: (value: string) => compoundCaseToTitleCase(value as string),
                        }}
                      >
                        {Object.entries(PriceCalculation).map(([, value]) => (
                          <MenuItem key={value} value={value} />
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={displayPricingCalculation ? 6 : 12}>
                  <FormControl fullWidth>
                    <Field
                      name={field}
                      autoComplete="off"
                      component={InputField}
                      label={!isExpressionField ? compoundCaseToTitleCase(field) : ''}
                      variant="filled"
                      className={classes.field}
                      InputProps={{
                        multiline: isExpressionField,
                        minRows: 5,
                        maxRows: 5,
                        style: isExpressionField ? { padding: '12px 8px', fontFamily: 'monaco' } : {},
                      }}
                      disabled={isExpressionField || (isPriceField && expressionExists)}
                    />
                    {isExpressionField && (
                      <FormHelperText>
                        <Typography
                          component="span"
                          className={classes.helperText}
                          dangerouslySetInnerHTML={{
                            __html: t(I18nKeys.PricingComponentPriceExpressionHelp),
                          }}
                        />
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            );
          }

          return null;
        })}
      </Grid>
    </Form>
  );
};

export const PricingComponentEditForm = reduxForm<FormData, CustomProps>({
  form: Forms.PricingComponentEdit,
})(PricingComponentEditFormComponent);
